import { useState } from 'react';
import { AppBar, Box, Button, Container, IconButton, makeStyles, Modal, Toolbar, Typography } from '@material-ui/core';
import theme from 'App/theme';
import { useUpdatePublic } from 'client/hooks/videoExport/useUpdatePublic';
import { useNotifier } from 'common/hooks/useNotifier';
import Message from 'common/models/Message';
import { CloseIcon } from 'common/utils/Icons';

interface ExportPublicButtonProps {
    isPublic: boolean;
    title: string;
    isComplete: boolean;
    jobId: string;
}

const useStyles = makeStyles({
    outlinedWhite: {
        '& .MuiFormLabel-root': {
            color: 'white !important',
        },
        '& > .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'white !important',
            },
            color: 'white !important',
        },
    },
    button: {
        width: '10px',
        height: '30px',
    },
    header: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
    modalContainer: {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 350,
        height: 'auto',
        backgroundColor: 'white',
        border: '2px solid #000',
        boxShadow: '24',
        padding: 4,
    },
    flexGrow: { flexGrow: 1 },
    closeIcon: {
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 99999,
        color: 'white',
        opacity: 1,
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        '& > *': {
            margin: theme.spacing(1),
        },
    },
});

export const ExportPublicButton: React.FC<ExportPublicButtonProps> = ({ isPublic, title, isComplete, jobId }) => {
    const [open, setOpen] = useState(false);
    const classes = useStyles();
    const { updatePublic, isLoading } = useUpdatePublic();
    const { notify } = useNotifier();

    const disabled = !isComplete || isLoading;

    const handleYes = async () => {
        try {
            await updatePublic({ isPublic: !isPublic, jobId });
            notify(new Message({ title: `Successfully ${isPublic ? 'unpublished.' : 'published.'}`, type: 'success' }));
            setOpen(false);
        } catch (error) {
            notify(new Message({ title: 'An error occurred, please try again.', type: 'error' }));
        }
    };

    return (
        <>
            <Button disabled={disabled} onClick={() => setOpen(true)}>
                {isPublic ? 'Yes' : 'No'}
            </Button>
            <Modal open={open} onClose={() => setOpen(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className={classes.modalContainer}>
                    <AppBar>
                        <Toolbar className={classes.header}>
                            <Typography variant="h6" component="div" className={classes.flexGrow}>
                                {isPublic ? 'Unpublish' : 'Publish'} {title}?
                            </Typography>
                            <IconButton aria-label="close" onClick={() => setOpen(false)} className={classes.closeIcon}>
                                <CloseIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <Toolbar />
                    <Container className={classes.container}>
                        <Typography variant="body1" component="div">
                            Are you sure you want to {isPublic ? 'unpublish' : 'publish'} {title}?
                            <br />
                            <br />
                            {isPublic && <>This will remove it from the doctor profile listing.</>}
                            {!isPublic && (
                                <>
                                    <strong>Warning:</strong> This will add it to the doctor profile listing and other users and the public will be
                                    able to watch it.
                                </>
                            )}
                        </Typography>
                        <Container className={classes.buttonContainer}>
                            <Button disabled={disabled} variant="contained" color="primary" onClick={() => handleYes()}>
                                Yes
                            </Button>
                            <Button variant="contained" color="primary" onClick={() => setOpen(false)}>
                                No
                            </Button>
                        </Container>
                    </Container>
                </Box>
            </Modal>
        </>
    );
};
